import { useTranslation } from 'react-i18next';
import { FiAlertCircle, FiX } from 'react-icons/fi';
import {
  DASH,
  fmtDelta,
  fmtIndex,
  fmtPercent,
  levelStyle,
} from '../../Utils/awarenessDepartments';

/**
 * Drill-down dal reparto ai moduli del percorso, costruito sulla heatmap già
 * scaricata: nessuna chiamata aggiuntiva quando si apre il dettaglio.
 */
export default function DepartmentDrilldown({
  department,
  heatmapDepartment,
  loadingModules = false,
  levels,
  smallSampleUsers,
  target,
  onClose,
}) {
  const { t } = useTranslation();

  if (!department) return null;

  const style = levelStyle(department.level, levels);
  const outOfTarget =
    target !== null && target !== undefined &&
    department.awareness_index !== null && department.awareness_index !== undefined &&
    Number(department.awareness_index) < Number(target);

  // Prima i moduli meno coperti: sono quelli su cui intervenire.
  const modules = [...(heatmapDepartment?.modules || [])].sort((a, b) => {
    const ar = a?.completionRate;
    const br = b?.completionRate;
    if (ar === null || ar === undefined) return 1;
    if (br === null || br === undefined) return -1;
    return Number(ar) - Number(br);
  });

  const metrics = [
    { label: t('awarenessDepartments.activeUsers'), value: department.users_count ?? DASH },
    { label: t('awarenessDepartments.completion'), value: fmtPercent(department.completion_rate) },
    { label: t('awarenessDepartments.quizScore'), value: fmtPercent(department.average_quiz_score) },
    { label: t('awarenessDepartments.phishingClickRate'), value: fmtPercent(department.phishing_click_rate) },
    { label: t('awarenessDepartments.vsAverage'), value: fmtDelta(department.vs_company_average) },
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full mx-2 sm:mx-4 max-h-[90vh] overflow-y-auto">
        <div className="p-4 sm:p-6">
          <div className="flex items-start justify-between gap-4 mb-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                {department.department_name || DASH}
              </h3>
              <div className="flex items-center gap-2 mt-1 flex-wrap">
                <span className="text-2xl font-bold text-gray-900">{fmtIndex(department.awareness_index)}</span>
                <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${style.badge}`}>
                  {t(`awarenessDepartments.levels.${department.level}`, department.level || DASH)}
                </span>
                {target !== null && target !== undefined && (
                  <span
                    className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                      outOfTarget ? 'bg-red-100 text-red-800' : 'bg-emerald-100 text-emerald-800'
                    }`}
                  >
                    {outOfTarget
                      ? t('awarenessDepartments.outOfTarget', { target })
                      : t('awarenessDepartments.onTarget', { target })}
                  </span>
                )}
                {department.small_sample && (
                  <span className="text-[11px] text-gray-500 border border-gray-300 rounded px-1.5 py-0.5">
                    {t('awarenessDepartments.smallSampleHint', { count: smallSampleUsers })}
                  </span>
                )}
              </div>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 shrink-0">
              <FiX className="w-6 h-6" />
            </button>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-5">
            {metrics.map((m) => (
              <div key={m.label} className="bg-gray-50 rounded-lg p-2 text-center">
                <div className="text-sm font-bold text-gray-900">{m.value}</div>
                <div className="text-[11px] text-gray-500">{m.label}</div>
              </div>
            ))}
          </div>

          <h4 className="text-sm font-semibold text-gray-700 mb-2">
            {t('awarenessDepartments.modulesBreakdown')}
          </h4>

          {loadingModules ? (
            <div className="p-3 bg-gray-50 rounded-lg animate-pulse space-y-2">
              <div className="h-3 bg-gray-200 rounded w-full" />
              <div className="h-3 bg-gray-200 rounded w-5/6" />
              <div className="h-3 bg-gray-200 rounded w-2/3" />
            </div>
          ) : modules.length === 0 ? (
            <div className="text-sm text-gray-500 flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
              <FiAlertCircle className="w-4 h-4" />
              {t('awarenessDepartments.noModulesForDepartment')}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      {t('awarenessDepartments.module')}
                    </th>
                    <th className="px-3 py-2 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      {t('awarenessDepartments.completedUsers')}
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider w-1/3">
                      {t('awarenessDepartments.moduleCompletion')}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {modules.map((module) => {
                    const rate = module.completionRate;
                    const hasRate = rate !== null && rate !== undefined;
                    return (
                      <tr key={module.id} className="hover:bg-gray-50">
                        <td className="px-3 py-2 text-sm text-gray-800">{module.title || DASH}</td>
                        <td className="px-3 py-2 text-sm text-gray-600 text-center">
                          {module.completedUsers ?? DASH}
                        </td>
                        <td className="px-3 py-2">
                          <div className="flex items-center gap-2">
                            <div className="flex-1 bg-gray-100 rounded-full h-2 overflow-hidden">
                              <div
                                className={`h-2 rounded-full ${hasRate && Number(rate) >= 60 ? 'bg-emerald-500' : 'bg-amber-500'}`}
                                style={{ width: hasRate ? `${Math.min(100, Math.max(0, Number(rate)))}%` : '0%' }}
                              />
                            </div>
                            <span className="text-xs font-medium text-gray-700 w-14 text-right">
                              {fmtPercent(rate)}
                            </span>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          <div className="mt-6 flex justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-800 rounded-lg font-medium"
            >
              {t('awarenessPage.close')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
